import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    RadioGroup,
    FormControlLabel,
    FormGroup,
    Radio,
    Checkbox,
    Box,
    Button,
    Divider,
} from "@mui/material";
import { SUBSCRIPTION_PLANS } from "./marketingConstants";
import ScheduleDateTimePicker from "./ScheduleDateTimePicker";

export default function PublishMessageModal({
    open,
    onClose,
    onConfirm,
    popupMessageType,
    popup_message_type,
}) {
    const rawType = popupMessageType ?? popup_message_type;
    const isInsight = rawType === "insight";

    const [publishOption, setPublishOption] = useState(
        isInsight ? "later" : "now",
    );
    const [scheduleDateTime, setScheduleDateTime] = useState(null);
    const [targetPlans, setTargetPlans] = useState([]);

    useEffect(() => {
        if (open) {
            setPublishOption(isInsight ? "later" : "now");
        }
    }, [open, isInsight]);

    const effectivePublishOption = isInsight ? "later" : publishOption;
    const isScheduleIncomplete =
        effectivePublishOption === "later" && !scheduleDateTime;
    const isPlanSelectionEmpty = targetPlans.length === 0;
    const isConfirmDisabled = isScheduleIncomplete || isPlanSelectionEmpty;

    const togglePlan = (plan) => {
        setTargetPlans((prev) =>
            prev.includes(plan)
                ? prev.filter((p) => p !== plan)
                : [...prev, plan],
        );
    };

    const handleConfirm = () => {
        if (isConfirmDisabled) return;

        onConfirm({
            publishOption: effectivePublishOption,
            scheduleDate: scheduleDateTime
                ? scheduleDateTime.format("YYYY-MM-DD")
                : "",
            scheduleTime: scheduleDateTime
                ? scheduleDateTime.format("HH:mm")
                : "",
            targetPlans,
        });
    };
    // Cancel button + Escape key both need to close without confirming.
    // Note: MUI's Dialog already closes on Escape by default via onClose
    // (reason: "escapeKeyDown") as long as disableEscapeKeyDown isn't set,
    // which it isn't here — so this works with no extra wiring.
    const handleCancel = () => onClose();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            sx={{ zIndex: 1300 }}
            PaperProps={{
                sx: {
                    backgroundColor: "#0d2147",
                    color: "#ffffff",
                    borderRadius: "12px",
                    maxWidth: "480px",
                    width: "100%",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    overflow: "visible",
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontWeight: 700,
                    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                }}
            >
                {isInsight ? "Publish Insight Message" : "Publish Marketing Message"}
            </DialogTitle>
            <DialogContent sx={{ pt: 2.5, overflow: "visible" }}>
                <Typography variant="body2" sx={{ color: "#b0c4de", mb: 2 }}>
                    {isInsight
                        ? "Select the time till which this insight should remain visible to active Vtrender users:"
                        : "Choose when this message should become visible to active Vtrender users:"}
                </Typography>

                <RadioGroup
                    value={effectivePublishOption}
                    onChange={(e) => {
                        if (!isInsight) {
                            setPublishOption(e.target.value);
                        }
                    }}
                >
                    <FormControlLabel
                        value="now"
                        disabled={isInsight}
                        control={
                            <Radio
                                sx={{
                                    color: "#3f69f8",
                                    "&.Mui-checked": { color: "#3f69f8" },
                                    "&.Mui-disabled": {
                                        color: "rgba(255, 255, 255, 0.25)",
                                    },
                                }}
                            />
                        }
                        label={
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: isInsight
                                        ? "rgba(255, 255, 255, 0.4)"
                                        : "inherit",
                                }}
                            >
                                Publish Immediately
                            </Typography>
                        }
                    />
                    <FormControlLabel
                        value="later"
                        control={
                            <Radio
                                sx={{
                                    color: "#3f69f8",
                                    "&.Mui-checked": { color: "#3f69f8" },
                                }}
                            />
                        }
                        label={
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                            >
                                {isInsight
                                    ? "Select the time till insight can be shown"
                                    : "Schedule for Later"}
                            </Typography>
                        }
                    />
                </RadioGroup>

                {effectivePublishOption === "later" && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 2,
                            backgroundColor: "#091730",
                            borderRadius: "8px",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            overflow: "visible",
                        }}
                    >
                        <ScheduleDateTimePicker
                            embeddedInModal
                            value={scheduleDateTime}
                            onChange={setScheduleDateTime}
                            placeholder={
                                isInsight
                                    ? "Select date and time till insight can be shown"
                                    : "Select publish date and time"
                            }
                        />
                        {isScheduleIncomplete && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "#ff6b6b",
                                    display: "block",
                                    mt: 1,
                                }}
                            >
                                {isInsight
                                    ? "Pick both a date and a time till which the insight can be shown."
                                    : "Pick both a date and a time before scheduling."}
                            </Typography>
                        )}
                    </Box>
                )}

                <Divider
                    sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        my: 2.5,
                    }}
                />

                <Typography variant="body2" sx={{ color: "#b0c4de", mb: 1 }}>
                    {isInsight
                        ? "Show this insight to subscribers on:"
                        : "Show this message to subscribers on:"}
                </Typography>

                <FormGroup
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        columnGap: 1,
                    }}
                >
                    {SUBSCRIPTION_PLANS.map((plan) => (
                        <FormControlLabel
                            key={plan}
                            control={
                                <Checkbox
                                    checked={targetPlans.includes(plan)}
                                    onChange={() => togglePlan(plan)}
                                    sx={{
                                        color: "#3f69f8",
                                        "&.Mui-checked": { color: "#3f69f8" },
                                    }}
                                />
                            }
                            label={
                                <Typography variant="body2">{plan}</Typography>
                            }
                        />
                    ))}
                </FormGroup>

                {isPlanSelectionEmpty && (
                    <Typography
                        variant="caption"
                        sx={{ color: "#ff6b6b", display: "block", mt: 0.5 }}
                    >
                        Select at least one plan to target.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    p: 2.5,
                    borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                    gap: 1,
                }}
            >
                <Button
                    onClick={handleCancel}
                    sx={{
                        color: "rgba(255, 255, 255, 0.7)",
                        textTransform: "none",
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={isConfirmDisabled}
                    sx={{
                        backgroundColor: "#3f69f8",
                        textTransform: "none",
                        fontWeight: 600,
                        px: 3,
                        "&:hover": { backgroundColor: "#2b52d9" },
                    }}
                >
                    {isInsight
                        ? "Confirm Schedule"
                        : publishOption === "now"
                        ? "Publish Now"
                        : "Confirm Schedule"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
